import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
/**
 * "Advanced: phone connection" — the Channels tab's collapsed details of how the phone reaches this
 * agent (design sync, the Claude Design export of 2026-10-09; the design's `advOpen`, `inPhone`,
 * `outPhone` and `disc*`).
 *
 * WHAT IS DRAWN DEPENDS ON THE CONNECTION, BECAUSE THE FACTS DO:
 *
 *   · **The account's own SIP trunk, answering calls** (`inPhone`): the provider's server addresses
 *     (the trunk's allow-list, editable), the address to send calls to (WeTalk's SIP host), the sign-in
 *     details — "Make new sign-in details" issues a new pair, shown once — the trunk's recent call
 *     activity, and "Disconnect this number".
 *   · **The account's own SIP trunk, making calls** (`outPhone`, a survey): the provider's server
 *     address, username, "Replace password", port and transport (editable, saved together), the number
 *     people see, the countries the agent may call, "Check connection" with its outcome in words,
 *     recent activity and "Disconnect this number".
 *   · **A number WeTalk issued** (a carrier number): the connection facts and the signalling trace the
 *     tab carried before this redesign, since a carrier number has no trunk settings to show.
 *   · **Nothing yet**: the trunk numbers of the account that no agent answers, to connect one here.
 *
 * "Disconnect this number" asks first ("Disconnect +39 …?") and unbinds every trunk number of this
 * agent; calls in progress finish first.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Dialog, Field, Icon, Input, Segmented, Table, TableBody, TableCell, TableRow, Textarea } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiErrorText } from "../../../../api/api-error.js";
import { useGateway } from "../../../../api/provider.js";
import {
  isInboundOnlyTrunkView,
  type SipTrunkEventView,
  type SipTrunkInboundAuthView,
  type SipTrunkView,
} from "../../../../api/slice/sip-trunk.js";
import { GLYPH } from "../../../../shell/glyph.js";
import { useMemberCan } from "../../../../shell/member-capability.js";
import { displayNumberOf } from "../../../numbers/number-display.js";
import { checkOutcomeWords, countryName, eventLineOf, TRANSPORT_WORD } from "../../../numbers/sip-trunk-copy.js";
import { ReplacePasswordDialog } from "../../../numbers/sip-trunk-dialog.js";
import { sipTrunkEventQuery, sipTrunkSettingsQuery } from "../../../numbers/sip-trunk-query.js";
import { SignInDetailRow, usePhoneConnectionRefresh } from "./phone-connect.js";
import type { PhoneConnectionView } from "./phone-connection.js";
import { inboundWords, registrationWordsOf, trunkStateWordOf } from "./sip-trunk-binding.js";

export interface AdvancedPhoneConnectionProps {
  readonly voiceAgentId: string;
  readonly phone: PhoneConnectionView;
  /** What the section holds when the number is not on the account's own trunk. */
  readonly carrierDetail: ReactNode;
}

export function AdvancedPhoneConnection({ voiceAgentId, phone, carrierDetail }: AdvancedPhoneConnectionProps): ReactNode {
  usePortalTranslation("operations");
  const [open, setOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const member = useMemberCan();
  const refusal = member.whyNot("sip_trunk.manage");
  const trunk = phone.trunkRow?.trunk ?? null;

  return (
    <>
      <div className="ch-advanced">
        <button
          type="button"
          className="ch-advanced-toggle"
          aria-expanded={open}
          aria-controls="phoneAdv"
          onClick={() => {
            setOpen(!open);
          }}
        >
          <Icon glyph={open ? GLYPH.caretDown : GLYPH.caretRight} size="sm" />
          {portalText("operations", "advanced_phone_connection_2bb1208d")}</button>
        {open ? (
          <div id="phoneAdv" className="ch-advanced-body">
            {trunk === null ? (
              carrierDetail
            ) : phone.outbound && isInboundOnlyTrunkView(trunk) ? (
              <p className="ch-advanced-value">
                {
                  portalText("operations", "this_connection_only_receives_calls_it_has_no_server_addre_5881c354")
                }
              </p>
            ) : phone.outbound ? (
              <CallingTrunkDetail voiceAgentId={voiceAgentId} trunk={trunk} e164={phone.e164} />
            ) : (
              <AnsweringTrunkDetail voiceAgentId={voiceAgentId} trunk={trunk} />
            )}
            {trunk === null ? null : (
              <>
                <RecentActivity trunk={trunk} inbound={!phone.outbound} />
                <div className="ch-advanced-foot">
                  {refusal === null ? (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setDisconnecting(true);
                      }}
                    >
                      <span className="ch-disconnect">{portalText("operations", "disconnect_this_number_815662a2")}</span>
                    </Button>
                  ) : (
                    <p className="ch-advanced-hint">{refusal}</p>
                  )}
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
      {disconnecting ? (
        <DisconnectDialog
          voiceAgentId={voiceAgentId}
          phone={phone}
          onClose={() => {
            setDisconnecting(false);
          }}
        />
      ) : null}
    </>
  );
}

function AnsweringTrunkDetail({ voiceAgentId, trunk }: { readonly voiceAgentId: string; readonly trunk: SipTrunkView }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const settings = useQuery(sipTrunkSettingsQuery(gateway));
  const refresh = usePhoneConnectionRefresh(voiceAgentId);
  const canManage = useMemberCan().can("sip_trunk.manage");
  const saved = trunk.allowedAddress.join("\n");
  const [allowed, setAllowed] = useState(saved);
  const [issued, setIssued] = useState<SipTrunkInboundAuthView | null>(null);

  const saveAllowed = useMutation({
    mutationFn: () =>
      gateway.updateSipTrunk(trunk.sipTrunkId, {
        allowedAddress: allowed
          .split("\n")
          .map((one) => one.trim())
          .filter((one) => one !== ""),
      }),
    onSuccess: refresh,
  });
  const issue = useMutation({
    mutationFn: () => gateway.createSipTrunkInboundAuth(trunk.sipTrunkId),
    onSuccess: async (auth) => {
      setIssued(auth);
      await refresh();
    },
  });

  return (
    <>
      <div className="ch-mono-field">
        <Field
          label={portalText("operations", "your_provider_s_server_addresses_933c425c")}
          description={portalText("operations", "ip_addresses_or_ranges_only_one_per_line_ask_your_provider_2ec94be8")}
          {...(saveAllowed.isError ? { error: apiErrorText(saveAllowed.error, portalText("operations", "the_addresses_were_not_saved_3373050d")) } : {})}
        >
          {(control) => (
            <Textarea
              {...control}
              rows={3}
              placeholder={"203.0.113.10\n203.0.113.0/28"}
              value={allowed}
              disabled={!canManage}
              onChange={(event) => {
                setAllowed(event.currentTarget.value);
              }}
            />
          )}
        </Field>
        {allowed === saved ? null : (
          <div className="c-row">
            <Button
              variant="secondary"
              disabled={saveAllowed.isPending}
              onClick={() => {
                saveAllowed.mutate();
              }}
            >
              {saveAllowed.isPending ? portalText("operations", "saving_56a2285c") : portalText("operations", "save_addresses_1b127e23")}
            </Button>
          </div>
        )}
      </div>

      <p className="ch-advanced-value" role="status">
        {inboundWords(trunk)}
        {registrationWordsOf(trunk) === null ? null : <> {registrationWordsOf(trunk)}.</>}
        {isInboundOnlyTrunkView(trunk) ? <> {trunkStateWordOf(trunk)}.</> : null}
      </p>

      <div className="field">
        <span className="ch-advanced-label">{portalText("operations", "address_to_send_calls_to_a84a525c")}</span>
        <span className="ch-copy-line">
          <code className="ch-code">{settings.data?.sipEdgeHost ?? "…"}</code>
          <CopyButton text={settings.data?.sipEdgeHost ?? ""} />
        </span>
      </div>

      <div className="field">
        <span className="ch-advanced-label">{portalText("operations", "sign_in_details_a4a630d1")}</span>
        {issued === null ? null : (
          <div role="status" className="ch-new-creds">
            {portalText("operations", "new_sign_in_details_are_ready_copy_them_into_your_provider_1c9eb693")}<dl className="c-va-pc-creds">
              <SignInDetailRow term={portalText("operations", "username_84c29015")} value={issued.inboundAuthUsername} />
              <SignInDetailRow term={portalText("operations", "password_8be3c943")} value={issued.inboundPassword} />
            </dl>
          </div>
        )}
        <span className="ch-creds-row">
          <Button
            variant="secondary"
            disabled={!canManage || issue.isPending}
            onClick={() => {
              issue.mutate();
            }}
          >
            {portalText("operations", "make_new_sign_in_details_84336743")}</Button>
          <span className="ch-advanced-hint">
            {trunk.inboundAuthUsername === null
              ? portalText("operations", "your_provider_signs_in_with_these_when_it_sends_calls_24e75972")
              : portalText("operations", "the_old_ones_stop_working_once_you_save_the_new_ones_at_yo_dcddac6a")}
          </span>
        </span>
        {issue.isError ? (
          <p className="c-note c-note-critical" role="alert">
            {apiErrorText(issue.error, portalText("operations", "new_sign_in_details_could_not_be_made_the_old_ones_still_w_fcdd0de1"))}
          </p>
        ) : null}
      </div>
    </>
  );
}

function CallingTrunkDetail({
  voiceAgentId,
  trunk,
  e164,
}: {
  readonly voiceAgentId: string;
  readonly trunk: SipTrunkView;
  readonly e164: string | null;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const settings = useQuery(sipTrunkSettingsQuery(gateway));
  const refresh = usePhoneConnectionRefresh(voiceAgentId);
  const canManage = useMemberCan().can("sip_trunk.manage");
  const [host, setHost] = useState(trunk.host);
  const [username, setUsername] = useState(trunk.authUsername);
  const [port, setPort] = useState(String(trunk.port));
  const [transport, setTransport] = useState(trunk.transport);
  const [replacing, setReplacing] = useState(false);
  const [checkMessage, setCheckMessage] = useState<{ readonly text: string; readonly good: boolean } | null>(null);

  const changed =
    host !== trunk.host || username !== trunk.authUsername || port !== String(trunk.port) || transport !== trunk.transport;

  const save = useMutation({
    mutationFn: () =>
      gateway.updateSipTrunk(trunk.sipTrunkId, {
        host: host.trim(),
        authUsername: username.trim(),
        port: Number(port),
        transport,
      }),
    onSuccess: refresh,
  });
  const check = useMutation({
    mutationFn: () => gateway.checkSipTrunk(trunk.sipTrunkId),
    onMutate: () => {
      setCheckMessage({ get text() { return portalText("operations", "placing_a_test_call_through_your_provider_fe118f44"); }, good: false });
    },
    onSuccess: async (checked) => {
      const words = checkOutcomeWords(checked.outcome, trunk, settings.data ?? null);
      setCheckMessage({
        get text() { return words.tone === "positive" ? portalText("operations", "connected_your_provider_accepted_a_test_call_227e26d9") : words.sentence; },
        good: words.tone === "positive",
      });
      await refresh();
    },
    onError: (raised: unknown) => {
      setCheckMessage({ get text() { return apiErrorText(raised, portalText("operations", "the_connection_could_not_be_checked_82c67f3d")); }, good: false });
    },
  });

  return (
    <>
      <div className="ch-mono-field">
        <Field label={portalText("operations", "your_provider_s_server_address_09c2e269")}>
          {(control) => (
            <Input
              {...control}
              value={host}
              disabled={!canManage}
              onChange={(event) => {
                setHost(event.currentTarget.value);
              }}
            />
          )}
        </Field>
      </div>
      <div className="ch-pair">
        <Field label={portalText("operations", "username_84c29015")}>
          {(control) => (
            <Input
              {...control}
              value={username}
              disabled={!canManage}
              onChange={(event) => {
                setUsername(event.currentTarget.value);
              }}
            />
          )}
        </Field>
        <div className="field">
          <span className="ch-advanced-label">{portalText("operations", "password_8be3c943")}</span>
          <span>
            <Button
              variant="secondary"
              disabled={!canManage}
              onClick={() => {
                setReplacing(true);
              }}
            >
              {portalText("operations", "replace_password_3f912c9c")}</Button>
          </span>
        </div>
      </div>
      <div className="ch-pair">
        <Field label={portalText("operations", "port_fe035157")}>
          {(control) => (
            <Input
              {...control}
              inputMode="numeric"
              value={port}
              disabled={!canManage}
              onChange={(event) => {
                setPort(event.currentTarget.value);
              }}
            />
          )}
        </Field>
        <div className="field">
          <Segmented
            name="advTrans"
            legend={portalText("operations", "transport_c10d76c9")}
            value={transport}
            options={(["udp", "tcp", "tls"] as const).map((one) => ({ value: one, label: TRANSPORT_WORD[one] }))}
            onChange={(next) => {
              setTransport(next);
            }}
          />
        </div>
      </div>
      {changed ? (
        <div className="c-row">
          <Button
            variant="secondary"
            disabled={save.isPending}
            onClick={() => {
              save.mutate();
            }}
          >
            {save.isPending ? portalText("operations", "saving_56a2285c") : portalText("operations", "save_connection_07796d38")}
          </Button>
          {save.isError ? (
            <span className="c-note c-note-critical" role="alert">
              {apiErrorText(save.error, portalText("operations", "the_connection_was_not_saved_192fa8b8"))}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="field">
        <span className="ch-advanced-label">{portalText("operations", "number_people_see_adbb1da7")}</span>
        <span className="ch-advanced-value">{e164 === null ? portalText("operations", "no_caller_id_number_yet_7c0afdf5") : displayNumberOf(e164)}</span>
      </div>
      <div className="field">
        <span className="ch-advanced-label">{portalText("operations", "countries_the_agent_may_call_f24d9fdf")}</span>
        <span className="ch-advanced-value">
          {trunk.allowedDestinationCountry.map(countryName).join(", ") || portalText("operations", "none_yet_80e9901f")}
          {(() => {
            const notYet = (settings.data?.destinationCountry ?? []).filter((one) => one.reviewedAt === null);
            return notYet.length === 0
              ? null
              : portalText("operations", "value_value_available_yet_2ac5ea0d", { v0: String(notYet.map((one) => countryName(one.code)).join(", ")), v1: String(notYet.length === 1 ? portalText("operations", "isn_t_ee2ebf51") : portalText("operations", "aren_t_624cd1f4")) });
          })()}
        </span>
      </div>
      <div className="c-row">
        <Button
          variant="secondary"
          disabled={check.isPending}
          onClick={() => {
            check.mutate();
          }}
        >
          {portalText("operations", "check_connection_14caeff0")}</Button>
        {checkMessage === null ? null : (
          <span role="status" className={checkMessage.good ? "ch-check-good" : "ch-check-note"}>
            {checkMessage.text}
          </span>
        )}
      </div>
      {replacing ? (
        <ReplacePasswordDialog
          trunk={trunk}
          onClose={() => {
            setReplacing(false);
          }}
          onReplaced={() => {
            void refresh();
          }}
        />
      ) : null}
    </>
  );
}

/** The result column of one activity line, in the design's words. */
export function activityResultOf(event: SipTrunkEventView): { readonly text: string; readonly good: boolean } {
  switch (event.kind) {
    case "answered":
      return { get text() { return portalText("operations", "answered_e0aafffa"); }, good: true };
    case "bye":
      return { get text() { return portalText("operations", "ended_90303d8d"); }, good: false };
    case "auth_failure":
    case "refused":
    case "unmatched_invite":
      return { get text() { return portalText("operations", "refused_735ec50d"); }, good: false };
    case "check":
      return { get text() { return portalText("operations", "checked_b2bc0c00"); }, good: false };
    case "state_changed":
      // plan-015 SIP (ADR 0046): an inbound-only trunk's first answered call verified it.
      return event.verifiedByFirstCall === true ? { get text() { return portalText("operations", "verified_aed3b8c6"); }, good: true } : { text: "—", good: false };
    case "final_response":
      if (event.responseCode === 486 || event.responseCode === 600) return { get text() { return portalText("operations", "busy_592a9d16"); }, good: false };
      if (event.responseCode !== null && event.responseCode >= 200 && event.responseCode < 300) return { get text() { return portalText("operations", "answered_e0aafffa"); }, good: true };
      return { get text() { return portalText("operations", "no_answer_a9c16dd0"); }, good: false };
    default:
      return { text: event.responseCode === null ? "—" : String(event.responseCode), good: false };
  }
}

function RecentActivity({ trunk, inbound }: { readonly trunk: SipTrunkView; readonly inbound: boolean }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const event = useQuery(sipTrunkEventQuery(gateway, trunk.sipTrunkId));
  const shown = (event.data ?? []).slice(0, 5);
  return (
    <div>
      <p className="ch-advanced-subtitle">{inbound ? portalText("operations", "recent_call_activity_afd89167") : portalText("operations", "recent_activity_72d5617f")}</p>
      {event.data === undefined ? (
        <p className="c-note">{event.isError ? portalText("operations", "the_activity_could_not_be_read_2749b3b2") : portalText("operations", "reading_the_activity_69558e57")}</p>
      ) : shown.length === 0 ? (
        <p className="c-note">{portalText("operations", "nothing_yet_calls_appear_here_as_they_come_and_go_d73e9e3d")}</p>
      ) : (
        <Table caption={portalText("operations", "recent_calls_on_this_connection_b0d00c0e")}>
          <TableBody>
            {shown.map((one) => {
              const result = activityResultOf(one);
              return (
                <TableRow key={one.sipTrunkEventId}>
                  <TableCell>
                    <span className="ch-activity-time">{one.atText}</span>
                  </TableCell>
                  <TableCell>{eventLineOf(one)}</TableCell>
                  <TableCell numeric>
                    <span className={result.good ? "ch-activity-good" : "ch-activity-muted"}>{result.text}</span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function DisconnectDialog({
  voiceAgentId,
  phone,
  onClose,
}: {
  readonly voiceAgentId: string;
  readonly phone: PhoneConnectionView;
  readonly onClose: () => void;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const refresh = usePhoneConnectionRefresh(voiceAgentId);
  const disconnect = useMutation({
    mutationFn: async () => {
      for (const row of phone.boundTrunkRow) {
        await gateway.unbindSipTrunkNumber(row.trunk.sipTrunkId, row.number.phoneNumberId);
      }
    },
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog
      open
      title={portalText("operations", "disconnect_value_cc69db18", { v0: String(phone.e164 === null ? portalText("operations", "this_number_7791794d") : displayNumberOf(phone.e164)) })}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {portalText("operations", "keep_it_connected_4fc80630")}</Button>
          <span className="ch-disconnect-confirm">
            <Button
              variant="primary"
              disabled={disconnect.isPending}
              onClick={() => {
                disconnect.mutate();
              }}
            >
              {portalText("operations", "disconnect_ed28e068")}</Button>
          </span>
        </>
      }
    >
      <p className="ch-disconnect-dek">
        {phone.outbound
          ? portalText("operations", "the_agent_stops_calling_from_this_number_straight_away_cal_8b1a09e4")
          : portalText("operations", "the_agent_stops_answering_calls_to_this_number_straight_aw_db8d1c96")}
      </p>
      {disconnect.isError ? (
        <p className="c-note c-note-critical" role="alert">
          {apiErrorText(disconnect.error, portalText("operations", "the_number_was_not_disconnected_nothing_changed_d1cb5c7e"))}
        </p>
      ) : null}
    </Dialog>
  );
}

function CopyButton({ text }: { readonly text: string }): ReactNode {
  usePortalTranslation("operations");
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="secondary"
      disabled={text === ""}
      onClick={() => {
        const clipboard = globalThis.navigator.clipboard as Clipboard | undefined;
        void clipboard?.writeText(text).then(() => {
          setCopied(true);
        });
      }}
    >
      {copied ? portalText("operations", "copied_8e3df45a") : portalText("operations", "copy_af74f7c5")}
    </Button>
  );
}
